import { pushLog } from './log-store';
import { stringifyArgs } from './serialize';
import type { LogLevel } from './types';

const LEVELS: LogLevel[] = ['log', 'info', 'warn', 'error'];

let installed = false;

/**
 * Patches the global console so every call is mirrored into the debug ring
 * buffer. Replaces the old `if (!__DEV__) console.log = () => {}` block in
 * `index.js`: in dev we still forward to the native console; in production we
 * keep the native console silent (as before, for perf) but retain the entry in
 * the buffer so testers can capture it via the profile long-press panel.
 *
 * Idempotent and self-guarding: a failure inside the patched method never
 * propagates to the caller.
 */
export function installConsoleCapture() {
  if (installed) return;
  installed = true;

  LEVELS.forEach(level => {
    // eslint-disable-next-line no-console
    const original = console[level].bind(console) as (...args: unknown[]) => void;
    // eslint-disable-next-line no-console
    console[level] = (...args: unknown[]) => {
      if (__DEV__) {
        original(...args);
      }
      try {
        pushLog({ kind: 'console', level, message: stringifyArgs(args) });
      } catch {
        // logging is best-effort
      }
    };
  });

  installGlobalErrorCapture();
}

/**
 * Captures uncaught JS exceptions (the ones that crash to the red screen / native
 * fatal) into the debug buffer, then chains to the previous global handler so
 * Sentry and the crash reporter still fire. Console errors and `captureException`
 * cover *reported* errors; this is the only path that records ones nobody caught.
 *
 * Note: this does not cover unhandled promise rejections — those go through RN's
 * separate rejection tracker, not `ErrorUtils`.
 */
function installGlobalErrorCapture() {
  try {
    const errorUtils = (globalThis as { ErrorUtils?: GlobalErrorUtils }).ErrorUtils;
    if (!errorUtils?.setGlobalHandler) return;

    const previous = errorUtils.getGlobalHandler?.();
    errorUtils.setGlobalHandler((error: unknown, isFatal?: boolean) => {
      try {
        const err = error as Error | undefined;
        const label = isFatal ? 'uncaught fatal' : 'uncaught';
        const stack = err?.stack ? `\n${err.stack}` : '';
        pushLog({
          kind: 'console',
          level: 'error',
          message: `[${label}] ${err?.name ?? 'Error'}: ${err?.message ?? String(error)}${stack}`,
        });
      } catch {
        // logging is best-effort
      }
      previous?.(error, isFatal);
    });
  } catch {
    // never let logging setup break startup
  }
}

type GlobalErrorUtils = {
  setGlobalHandler?: (handler: (error: unknown, isFatal?: boolean) => void) => void;
  getGlobalHandler?: () => ((error: unknown, isFatal?: boolean) => void) | undefined;
};
